import { type ComponentType, type MouseEvent } from 'react';
import {
    ClockIcon,
    FileVideoIcon,
    type IconProps,
    UserIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Image } from '@/components';
import {
    DirectoryCard,
    type DirectoryCardMetadataItem,
} from '@/components/DirectoryCard';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Person } from '@/types/person';
import { parseEngineSourceType, SourceType } from '@/types/source';
import { formatDate, formatRelativeTime } from '@/utils/date';
import { SecurityCameraIconMirrored } from '@/utils/icons';

const SOURCE_TYPE_ICONS: Record<SourceType, ComponentType<IconProps>> = {
    [SourceType.CAMERA]: SecurityCameraIconMirrored,
    [SourceType.FLIR]: SecurityCameraIconMirrored,
    [SourceType.FILE]: FileVideoIcon,
};

interface PersonCardProps {
    person: Person;
    checked?: boolean;
    isDeleting?: boolean;
    selectionDisabled?: boolean;
    onClick?: (id: string, event?: MouseEvent<HTMLDivElement>) => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onMarkAsRecognized?: (id: string) => void;
    onMarkAsUnknown?: (id: string) => void;
    onMerge?: () => void;
}

export function PersonCard({
    person,
    checked,
    isDeleting,
    selectionDisabled,
    onClick,
    onEdit,
    onDelete,
    onMarkAsRecognized,
    onMarkAsUnknown,
    onMerge,
}: PersonCardProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const displayName = person.name || t('persons.card.unknownPerson');

    const lastEvent = person.lastEvent;
    const hasLastEvent = lastEvent != null;
    const seenAt = hasLastEvent
        ? (person.lastSeen ?? person.createdAt)
        : person.createdAt;
    const sourceName = hasLastEvent
        ? (lastEvent.sourceName ?? person.lastSourceName ?? '')
        : '';
    const showSource = hasLastEvent && sourceName.length > 0;
    const sourceKind = showSource
        ? parseEngineSourceType(lastEvent.sourceType ?? person.lastSourceType)
        : undefined;
    const SourceIcon = sourceKind
        ? SOURCE_TYPE_ICONS[sourceKind]
        : SecurityCameraIconMirrored;

    const tagLabels = person.tags ?? [];

    const metadata: DirectoryCardMetadataItem[] = [
        {
            icon: ClockIcon,
            text: t(
                hasLastEvent ? 'persons.card.seenAgo' : 'persons.card.addedAgo',
                { time: formatRelativeTime(seenAt) },
            ),
            title: formatDate(seenAt),
            textClassName: 'lowercase first-letter:uppercase',
        },
        ...(showSource
            ? [{ icon: SourceIcon, text: sourceName, title: sourceName }]
            : []),
    ];

    const image = (
        <>
            {person.imageUrl ? (
                <Image
                    src={person.imageUrl}
                    alt={displayName}
                    auth
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="w-full h-full bg-grey-600 flex items-center justify-center">
                    <UserIcon className="text-grey-300" size={32} />
                </div>
            )}
            {checked !== undefined && (
                <div className="absolute top-1 left-1 z-10 pointer-events-none">
                    <Checkbox checked={checked} disabled={selectionDisabled} />
                </div>
            )}
        </>
    );

    return (
        <DirectoryCard
            testId="person-card"
            title={displayName}
            image={image}
            metadata={metadata}
            tagLabels={tagLabels}
            isKnown={person.known}
            isDeleting={isDeleting}
            disabled={selectionDisabled}
            actions={{
                editLabel: t('persons.card.edit'),
                deleteLabel:
                    !person.known && onDelete
                        ? t('persons.card.delete')
                        : undefined,
                markAsUnknownLabel: person.known
                    ? t('persons.card.markAsUnknown')
                    : undefined,
                mergeLabel: t('mergeIdentityModal.mergeInto'),
                onEdit: onEdit ? () => onEdit(person.id) : undefined,
                onDelete:
                    !person.known && onDelete
                        ? () => onDelete(person.id)
                        : undefined,
                onMarkAsUnknown:
                    person.known && onMarkAsUnknown
                        ? () => onMarkAsUnknown(person.id)
                        : undefined,
                onMerge,
            }}
            markAsRecognizedLabel={t('persons.card.markAsRecognized')}
            onClick={onClick ? (event) => onClick(person.id, event) : undefined}
            onMarkAsRecognized={
                onMarkAsRecognized
                    ? () => onMarkAsRecognized(person.id)
                    : undefined
            }
        />
    );
}
